<template>
    <span v-if="plain">{{ plainGradesText }}</span>
    <div v-else class="flex flex-wrap gap-2">
        <template v-if="hasK12(processedGrades)">
            <span :style="{ backgroundColor: showColors? gradeColorMap('K-12') : '' }" class="px-3 py-1.5 rounded-full uppercase text-xs font-medium whitespace-nowrap flex-shrink-0">
                K-12
            </span>
            <span v-for="grade in nonK12Grades" :key="grade" :style="{ backgroundColor: showColors?  gradeColorMap(grade) : '' }" class="px-2 py-1.5 rounded-full uppercase text-xs font-medium whitespace-nowrap flex-shrink-0">
                {{ grade }}
            </span>
        </template>
        <template v-else>
            <span v-for="grade in processedGrades" :key="grade" :style="{ backgroundColor: showColors?  gradeColorMap(grade) : '' }" class="px-2 py-1.5 rounded-full uppercase text-xs font-medium whitespace-nowrap flex-shrink-0">
                {{ grade }}
            </span>
        </template>
    </div>
</template>

<script>
import { sortGradeBands } from '../Helpers/Utils.js';

const gradeColorMap = (grade) => {
  // Return default color if grade is undefined or null
  if (!grade) {
    return '#FFEDB8';
  }

  // Handle grade ranges (e.g., "3-8", "4-10", etc.)
  if (grade.includes('-')) {
    return '#FFEDB8';
  }

  return {
    'PK': '#FFEDB8',
    'K': '#FFEDB8',
    '1': '#FFEDB8',
    '2': '#FFEDB8',
    '3': '#FFEDB8',
    '4': '#FFEDB8',
    '5': '#FFEDB8',
    '6': '#FFEDB8',
    '7': '#FFEDB8',
    '8': '#FFEDB8',
    '9': '#FFEDB8',
    '10': '#FFEDB8',
    '11': '#FFEDB8',
    '12': '#FFEDB8',
    'AP': '#FFEDB8',
    'K-12': '#FFEDB8',
  }[grade] || '#FFEDB8' // Default fallback color
}

const hasK12 = (grades) => {
    if(!grades) {
        return false;
    }
  const k12Grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  return k12Grades.every(grade => grades.includes(grade));
}

const mergeConsecutiveGrades = (grades) => {
    if (!grades || grades.length <= 7) {
        return [...grades].sort(sortGradeBands);
    }

    // Sort grades first
    const sortedGrades = [...grades].sort(sortGradeBands);

    // Convert grades to numeric values for easier processing
    const gradeToNumber = (grade) => {
        if (grade === 'PK') return -1;
        if (grade === 'K') return 0;
        if (grade === 'AP') return 13; // Higher than 12
        return parseInt(grade);
    };

    const numberToGrade = (num) => {
        if (num === -1) return 'PK';
        if (num === 0) return 'K';
        if (num === 13) return 'AP';
        return num.toString();
    };

    const numericGrades = sortedGrades.map(gradeToNumber);
    const merged = [];
    let start = numericGrades[0];
    let end = numericGrades[0];

    for (let i = 1; i < numericGrades.length; i++) {
        if (numericGrades[i] === end + 1) {
            end = numericGrades[i];
        } else {
            // Add the current range
            if (start === end) {
                merged.push(numberToGrade(start));
            } else {
                merged.push(`${numberToGrade(start)}-${numberToGrade(end)}`);
            }
            start = numericGrades[i];
            end = numericGrades[i];
        }
    }

    // Add the last range
    if (start === end) {
        merged.push(numberToGrade(start));
    } else {
        merged.push(`${numberToGrade(start)}-${numberToGrade(end)}`);
    }

    // Sort the final result to ensure proper order
    return merged.sort(sortGradeBands);
};

export default {
    props: {
        grades: {
            type: Array,
            required: true
        },
        showColors: {
            type: Boolean,
            default: true
        },
        plain: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        processedGrades() {
            if (!this.grades) return [];
            return mergeConsecutiveGrades(this.grades);
        },
        nonK12Grades() {
            if(!this.processedGrades) {
                return [];
            }
            const k12Grades = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
            return this.processedGrades.filter(grade => !k12Grades.includes(grade));
        },
        plainGradesText() {
            if (!this.processedGrades.length) return '';
            if (this.hasK12(this.processedGrades)) {
                return ['K-12', ...this.nonK12Grades].join(', ');
            }
            return this.processedGrades.join(', ');
        }
    },
    methods: {
        gradeColorMap,
        sortGradeBands,
        hasK12
    }
}
</script>
